import React from 'react';

const regionLabels = {
  north_america: 'North America',
  europe: 'Europe',
  asia_pacific: 'Asia Pacific',
  china: 'China',
  latin_america: 'Latin America',
  other: 'Other',
};

const regionColors = {
  north_america: 'bg-coros-status-info',
  europe: 'bg-coros-status-success',
  asia_pacific: 'bg-coros-ai',
  china: 'bg-coros-status-error',
  latin_america: 'bg-coros-status-warning',
  other: 'bg-coros-bg-tertiary',
};

export function RegionalBreakdown({ data }) {
  if (!data) return null;

  const { regional_breakdown, top_countries, insight, total_tickets } = data;

  // Sort regions by count
  const sortedRegions = Object.entries(regional_breakdown || {})
    .sort((a, b) => b[1].count - a[1].count)
    .filter(([, value]) => value.count > 0);

  return (
    <div className="card">
      <div className="flex items-center space-x-2 mb-4">
        <span className="text-xl">🌍</span>
        <h3 className="text-lg font-semibold text-coros-text-primary">Regional Breakdown</h3>
      </div>

      {/* Bar chart */}
      {sortedRegions.length === 0 && (
        <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>No regional data available for this cluster.</p>
      )}
      <div className="space-y-3 mb-6">
        {sortedRegions.map(([region, value]) => (
          <div key={region}>
            <div className="flex items-center justify-between text-sm mb-1">
              <span className="text-coros-text-secondary">{regionLabels[region] || region}</span>
              <span className="text-coros-text-muted">
                {value.percentage}% ({value.count})
              </span>
            </div>
            <div className="h-2 bg-coros-bg-tertiary rounded-full overflow-hidden">
              <div
                className={`h-full ${regionColors[region] || 'bg-coros-bg-tertiary'} transition-all duration-500`}
                style={{ width: `${value.percentage}%` }}
              />
            </div>
          </div>
        ))}
      </div>

      {/* Insight */}
      {insight && (
        <div className="bg-coros-bg-tertiary rounded-lg p-4 mb-4">
          <p className="text-sm text-coros-text-secondary">
            <span className="text-coros-status-warning mr-2">💡</span>
            {insight}
          </p>
        </div>
      )}

      {/* Top countries */}
      {top_countries && top_countries.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-coros-text-muted mb-2">Top Countries</h4>
          <div className="flex flex-wrap gap-2">
            {top_countries.slice(0, 5).map((country) => (
              <span
                key={country.country}
                className="inline-flex items-center px-2 py-1 bg-coros-bg-tertiary rounded text-sm text-coros-text-secondary"
              >
                {country.country} ({country.count})
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function RegionalSummary({ summary }) {
  if (!summary) return null;

  return (
    <div className="card">
      <div className="flex items-center space-x-2 mb-4">
        <span className="text-xl">🌍</span>
        <h3 className="text-lg font-semibold text-coros-text-primary">Regional Summary</h3>
      </div>

      {summary.top_region && (
        <div className="mb-4">
          <p className="text-coros-text-secondary">
            Top region: <span className="text-coros-text-primary font-medium">{summary.top_region}</span>
            {summary.top_region_percentage && (
              <span className="text-coros-text-muted ml-1">
                ({summary.top_region_percentage}%)
              </span>
            )}
          </p>
        </div>
      )}

      {summary.region_breakdown && Object.keys(summary.region_breakdown).length > 0 ? (
        <div className="grid grid-cols-2 gap-2">
          {Object.entries(summary.region_breakdown)
            .sort((a, b) => b[1] - a[1])
            .map(([region, percentage]) => (
              <div key={region} className="flex items-center justify-between text-sm">
                <span className="text-coros-text-muted">{regionLabels[region] || region}</span>
                <span className="text-coros-text-secondary">{percentage}%</span>
              </div>
            ))}
        </div>
      ) : !summary.top_region && (
        <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>No regional data available yet.</p>
      )}
    </div>
  );
}

export default RegionalBreakdown;
